import { Column } from 'further-ui/components/Layout';
import { useScrollSpy } from './useScrollSpy';
import { Box, ButtonBase, Menu, styled } from '@mui/material';
import React, { useState } from 'react';
import VerticalTabsMenu from 'components/new/VerticalTabs/Menu';

const Rectangle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active, theme }) => ({
  height: '2px',
  width: '16px',
  backgroundColor: active ? theme.palette.grey[900] : theme.palette.grey[200],
  borderRadius: '100px',
  transition: 'all 150ms ease',
}));

type StepperProps = {
  sections: Array<{ id: string; label: string }>;
  intersectionRootMargin?: string;
};

const ScrollStepper: React.FC<StepperProps> = ({
  sections,
  intersectionRootMargin,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const activeId = useScrollSpy(
    sections.map((s) => s.id),
    {
      rootMargin: intersectionRootMargin,
    },
  );

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (open) return;
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: {
    preventDefault?: () => void;
    stopPropagation?: () => void;
  }) => {
    if (!open) return;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  return (
    <ButtonBase
      disableTouchRipple
      onClick={handleOpen}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
    >
      <Column spacing="sm">
        {sections.map((section) => {
          const active = section.id === activeId;
          return <Rectangle active={active} key={section.id} />;
        })}
      </Column>
      <Menu
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorEl={anchorEl}
        open={open}
        hideBackdrop
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: { pointerEvents: 'auto' },
            onMouseLeave: handleClose,
          },
        }}
      >
        <Box
          sx={(theme) => ({ padding: theme.spacing(theme.spacingSizes.xs) })}
        >
          <VerticalTabsMenu
            onTabChange={(id) => scrollToSection(id)}
            tabs={sections}
            selectedTab={activeId ?? ''}
          />
        </Box>
      </Menu>
    </ButtonBase>
  );
};

export default ScrollStepper;
